import { LitElement, html } from 'lit'
import Quill from 'quill'
import quillCss from 'quill/dist/quill.snow.css'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// Rich-text comment editor built on Quill. Supports bold/italic/underline,
// H2/H3, ordered/bulleted lists, links, inline image upload, and file
// attachments. Emits nothing; the parent pulls { html, attachmentIds } via
// getContent() on submit.
//
// Rendered in LIGHT DOM because Quill manages its own DOM and its selection APIs
// misbehave across shadow boundaries. Quill's stylesheet is inlined as a <style>
// in the template so it applies wherever this element is placed — including
// inside a parent's shadow root, which a global stylesheet could not reach.
const CHROME_CSS = `
  rich-editor { display: block; }
  rich-editor .ql-toolbar.ql-snow, rich-editor .ql-container.ql-snow {
    border-color: var(--bx-border); }
  rich-editor .ql-toolbar.ql-snow { border-top-left-radius: var(--bx-radius); border-top-right-radius: var(--bx-radius); background: var(--bx-surface); }
  rich-editor .ql-container.ql-snow { border-bottom-left-radius: var(--bx-radius); border-bottom-right-radius: var(--bx-radius); }
  rich-editor .ql-editor { min-height: 110px; font-family: var(--bx-font); font-size: 0.95rem; color: var(--bx-ink); }
  /* A string with nowhere to break (a long URL, a token, a stack frame) must not set the
     editor's width. Quill ships white-space: pre-wrap + word-wrap: break-word, which wraps
     mid-string; we scroll instead so the text stays as typed. overflow-x is what keeps the
     overflow inside the pane — but on its own it does not stop a grid track from growing to
     the string, so the containers that hold an editor size their flexible track with
     minmax(0, …) (page-ticket.js, page-tickets.js). */
  rich-editor .ql-editor { overflow-x: auto; overflow-wrap: normal; word-wrap: normal; }
  rich-editor .ql-editor.ql-blank::before { color: var(--bx-muted); font-style: normal; }
  rich-editor .rt-attach { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; margin-top: 0.55rem; }
  rich-editor .rt-attach-btn { font: inherit; font-size: 0.82rem; cursor: pointer; padding: 0.35rem 0.7rem;
    border: 1px solid var(--bx-border); border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); }
  rich-editor .rt-chip { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.8rem;
    background: var(--bx-bg); border: 1px solid var(--bx-border); border-radius: 999px; padding: 0.2rem 0.3rem 0.2rem 0.65rem; }
  rich-editor .rt-chip button { border: none; background: none; cursor: pointer; color: var(--bx-muted); font: inherit; padding: 0 0.2rem; }
`

// Quill sends four document events to an editor only if
// document.querySelectorAll('.ql-container') finds that editor. This query does not
// go into shadow roots. Each rich-editor is in the shadow root of its parent, thus
// Quill does not see a selection that the user makes with the mouse or a shortcut.
// When the user clicks a toolbar button, Quill uses its last caret. The link button
// then finds no selected text and stops, and bold applies to an empty caret.
// This listener sends the events to each connected rich-editor.
const QUILL_DOM_EVENTS = ['selectionchange', 'mousedown', 'mouseup', 'click']
const connectedEditors = new Set()
for (const type of QUILL_DOM_EVENTS) {
  document.addEventListener(type, event => {
    connectedEditors.forEach(editor => editor._forwardQuillEvent(event))
  })
}

// Quill keeps a link URL as the user types it. Without a scheme, "brandextract.com"
// is a relative link, and the browser opens /tickets/brandextract.com. This Link
// format adds https:// to a URL that starts with a host name. A URL with a scheme
// (https:, mailto:), a path (/kb/5), an anchor (#top), or a query (?q=1) does not
// change. Quill uses sanitize() for new links, changed links, and loaded HTML.
const BARE_HOST = /^[\w-]+(\.[\w-]+)+(:\d+)?([/?#]|$)/
const QuillLink = Quill.import('formats/link')
class SchemeLink extends QuillLink {
  static sanitize(url) {
    if (typeof url === 'string' && BARE_HOST.test(url.trim())) url = `https://${url.trim()}`
    return super.sanitize(url)
  }
}
Quill.register(SchemeLink, true)

class RichEditor extends LitElement {
  static properties = {
    ticketNumber: { type: Number },
    placeholder: { type: String },
    initialHtml: { type: String },   // preload content when editing existing HTML
    allowVideo: { type: Boolean },   // add a video-embed button (KB articles)
    _attachments: { state: true }
  }

  createRenderRoot() { return this }  // light DOM

  constructor() {
    super()
    this.placeholder = 'Write a reply…'
    this._attachments = []
    this._quill = null
  }

  connectedCallback() {
    super.connectedCallback()
    connectedEditors.add(this)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    connectedEditors.delete(this)
  }

  firstUpdated() {
    // Mount Quill on an inner node inside the static host so all of Quill's
    // injected DOM (toolbar + container) stays isolated from Lit's dynamic parts.
    const host = this.querySelector('.rt-host')
    const inner = document.createElement('div')
    host.appendChild(inner)

    const toolbar = [
      ['bold', 'italic', 'underline'],
      [{ header: 2 }, { header: 3 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ...(this.ticketNumber ? [['image']] : []),
      ...(this.allowVideo ? [['video']] : [])
    ]
    // Keep the link tooltip in the editor box. The default bounds is document.body.
    // A parent with overflow: hidden (ticket-reply.js) then cuts off a tooltip that
    // goes past the left edge of the editor.
    this._quill = new Quill(inner, { theme: 'snow', placeholder: this.placeholder, bounds: inner, modules: { toolbar } })
    this._makeSelectionShadowAware(this._quill)
    if (this.ticketNumber) {
      this._quill.getModule('toolbar').addHandler('image', () => this._insertImage())
    }
    if (this.allowVideo) {
      this._quill.getModule('toolbar').addHandler('video', () => this._insertVideo())
    }
    // Preload existing HTML when editing (Quill's blessed HTML-set path).
    if (this.initialHtml) this._quill.clipboard.dangerouslyPasteHTML(this.initialHtml)
  }

  // Prompt for a video URL and embed it. Normalizes common YouTube/Vimeo share URLs
  // to their embed form so the iframe actually plays (and survives the KB sanitizer's
  // trusted-host allowlist).
  _insertVideo() {
    const url = (window.prompt('Video URL (YouTube or Vimeo)') || '').trim()
    if (!url) return
    const embed = this._toEmbedUrl(url)
    if (!embed) { toast('Enter a YouTube or Vimeo link', 'error'); return }
    const range = this._quill.getSelection(true)
    const index = range ? range.index : this._quill.getLength()
    this._quill.insertEmbed(index, 'video', embed, 'user')
    this._quill.setSelection(index + 1, 0)
  }

  _toEmbedUrl(url) {
    let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/)
    if (m) return `https://www.youtube.com/embed/${m[1]}`
    m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
    if (m) return `https://player.vimeo.com/video/${m[1]}`
    return null
  }

  // Quill's selection uses document.getSelection()/document.activeElement, which do
  // not pierce shadow DOM. This editor lives inside the host component's shadow root,
  // so Quill would read a null caret and insert every keystroke at index 0 (the first
  // typed char ends up last). Repoint selection reads at the editor's real root node
  // (the ShadowRoot, via getSelection()/activeElement) so the caret tracks correctly.
  _makeSelectionShadowAware(quill) {
    const sel = quill.selection
    const rootSelection = () => {
      const rn = quill.root.getRootNode()
      // Chrome/Safari expose ShadowRoot.getSelection(); Firefox reports shadow
      // ranges through document.getSelection(), so fall back to it.
      return (typeof rn.getSelection === 'function' ? rn.getSelection() : null) || document.getSelection()
    }

    sel.getNativeRange = function () {
      const selection = rootSelection()
      if (selection == null || selection.rangeCount <= 0) return null
      const nativeRange = selection.getRangeAt(0)
      if (nativeRange == null) return null
      return this.normalizeNative(nativeRange)
    }

    sel.hasFocus = function () {
      const rn = this.root.getRootNode()
      const active = rn.activeElement
      return active === this.root || (active != null && this.root.contains(active))
    }
  }

  // Send a document event to Quill (see QUILL_DOM_EVENTS).
  _forwardQuillEvent(event) {
    const quill = this._quill
    if (!quill) return
    const rn = quill.root.getRootNode()
    // Quill itself sends events to an editor in the main document. Do not send them two times.
    if (!(rn instanceof ShadowRoot)) return
    quill.emitter.handleDOM(event)
    // Quill hides the link tooltip on a click outside it. Its check uses
    // document.body.contains(), which is false in a shadow root, thus the check stops
    // before it hides the tooltip. Do the same check here with the shadow root.
    const { tooltip } = quill.theme
    if (event.type === 'click' && tooltip && !event.composedPath().includes(tooltip.root) &&
        rn.activeElement !== tooltip.textbox && !quill.hasFocus()) {
      tooltip.hide()
    }
  }

  _pickFile(accept) {
    return new Promise(resolve => {
      const input = document.createElement('input')
      input.type = 'file'
      if (accept) input.accept = accept
      input.onchange = () => resolve(input.files[0] || null)
      input.click()
    })
  }

  async _insertImage() {
    const file = await this._pickFile('image/*')
    if (!file) return
    try {
      const att = await api.upload(`/tickets/${this.ticketNumber}/attachments`, file, '?inline=1')
      const range = this._quill.getSelection(true)
      const index = range ? range.index : this._quill.getLength()
      this._quill.insertEmbed(index, 'image', att.url, 'user')
      this._quill.setSelection(index + 1, 0)
    } catch (err) {
      toast(err.message || 'Image upload failed', 'error')
    }
  }

  async _attachFile() {
    const file = await this._pickFile('')
    if (!file) return
    try {
      const att = await api.upload(`/tickets/${this.ticketNumber}/attachments`, file)
      this._attachments = [...this._attachments, att]
    } catch (err) {
      toast(err.message || 'Upload failed', 'error')
    }
  }

  _removeAttachment(id) {
    this._attachments = this._attachments.filter(a => a.id !== id)
  }

  // --- Parent API ---
  getContent() {
    return {
      html: this._quill ? this._quill.root.innerHTML : '',
      attachmentIds: this._attachments.map(a => a.id)
    }
  }

  isEmpty() {
    if (!this._quill) return true
    const hasText = this._quill.getText().trim().length > 0
    // An embed alone is real content — image, or a KB video/iframe embed. Mirrors the
    // server-side body check in kb.service.js (img|iframe|video).
    const hasEmbed = !!this._quill.root.querySelector('img, iframe, video')
    return !hasText && !hasEmbed && this._attachments.length === 0
  }

  clear() {
    if (this._quill) this._quill.setContents([])
    this._attachments = []
  }

  render() {
    return html`
      <style>${quillCss}${CHROME_CSS}</style>
      <div class="rt-host"></div>
      ${this.ticketNumber ? html`
        <div class="rt-attach">
          <button type="button" class="rt-attach-btn" @click=${this._attachFile}>📎 Attach file</button>
          ${this._attachments.map(a => html`
            <span class="rt-chip">${a.filename}
              <button type="button" title="Remove" @click=${() => this._removeAttachment(a.id)}>✕</button>
            </span>`)}
        </div>` : ''}
    `
  }
}

customElements.define('rich-editor', RichEditor)
